import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../components';
import { useAuth } from '../../hooks';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkIcon from '@mui/icons-material/Work';
import PaymentIcon from '@mui/icons-material/Payment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FlagIcon from '@mui/icons-material/Flag';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PercentIcon from '@mui/icons-material/Percent';

const SettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();

  const allSettingsCards = [
    {
      id: 'organizationHierarchy',
      title: t('settings.titles.organizationHierarchy'),
      description: t('settings.descriptions.organizationHierarchy'),
      icon: <AccountTreeIcon />,
      path: '/settings/organization-hierarchy',
      color: theme.palette.success.main,
      requiresRoot: true, // Only ROOT users can see this
    },
    {
      id: 'titles',
      title: t('settings.titles.titles'),
      description: t('settings.descriptions.titles'),
      icon: <SettingsIcon />,
      path: '/settings/titles',
      color: theme.palette.primary.main,
    },
    {
      id: 'roleManagement',
      title: t('settings.titles.roleManagement'),
      description: t('settings.descriptions.roleManagement'),
      icon: <AdminPanelSettingsIcon />,
      path: '/settings/role-management',
      color: theme.palette.secondary.main,
      requiresRoot: true, // Only ROOT users can see this
    },
    {
      id: 'leaveTypes',
      title: t('settings.titles.leaveTypes'),
      description: t('settings.descriptions.leaveTypes'),
      icon: <BeachAccessIcon />,
      path: '/settings/leave-types',
      color: theme.palette.info.main,
    },
    {
      id: 'expenseTypes',
      title: t('settings.titles.expenseTypes'),
      description: t('settings.descriptions.expenseTypes'),
      icon: <ReceiptIcon />,
      path: '/settings/expense-types',
      color: theme.palette.warning.main,
    },
    {
      id: 'partnerManagement',
      title: t('settings.titles.partnerManagement'),
      description: t('settings.descriptions.partnerManagement'),
      icon: <HandshakeIcon />,
      path: '/settings/partner-management',
      color: theme.palette.success.main,
    },
    {
      id: 'sectors',
      title: t('settings.titles.sectors'),
      description: t('settings.descriptions.sectors'),
      icon: <CategoryIcon />,
      path: '/settings/sectors',
      color: theme.palette.info.main,
    },
    {
      id: 'contractSubjects',
      title: t('settings.titles.contractSubjects'),
      description: t('settings.descriptions.contractSubjects'),
      icon: <DescriptionIcon />,
      path: '/settings/contract-subjects',
      color: theme.palette.primary.main,
    },
    {
      id: 'jobTypes',
      title: t('settings.titles.jobTypes', 'İş Türleri'),
      description: t(
        'settings.descriptions.jobTypes',
        'İş türlerini yönet (proje bazlı mı, durum grubu).'
      ),
      icon: <WorkIcon />,
      path: '/settings/job-types',
      color: theme.palette.secondary.main,
    },
    {
      id: 'revenueCalculationRules',
      title: t('settings.titles.revenueCalculationRules', 'Ciro Hesaplama Kuralları'),
      description: t(
        'settings.descriptions.revenueCalculationRules',
        'Cirolarım formunda tutarı otomatik hesaplayacak kuralları yönet.'
      ),
      icon: <PercentIcon />,
      path: '/settings/revenue-calculation-rules',
      color: theme.palette.warning.main,
    },
    {
      id: 'scopeOfWork',
      title: t('settings.titles.scopeOfWork'),
      description: t('settings.descriptions.scopeOfWork'),
      icon: <WorkIcon />,
      path: '/settings/scope-of-work',
      color: theme.palette.success.main,
    },
    {
      id: 'pricingMethods',
      title: t('settings.titles.pricingMethods'),
      description: t('settings.descriptions.pricingMethods'),
      icon: <PaymentIcon />,
      path: '/settings/pricing-methods',
      color: theme.palette.warning.main,
    },
    {
      id: 'workStatus',
      title: t('settings.titles.workStatus'),
      description: t('settings.descriptions.workStatus'),
      icon: <AssignmentIcon />,
      path: '/settings/work-status',
      color: theme.palette.secondary.main,
    },
    {
      id: 'reportTypes',
      title: t('settings.titles.reportTypes'),
      description: t('settings.descriptions.reportTypes'),
      icon: <AssessmentIcon />,
      path: '/settings/report-types',
      color: theme.palette.info.main,
    },
    {
      id: 'proposalStatuses',
      title: t('settings.titles.proposalStatuses'),
      description: t('settings.descriptions.proposalStatuses'),
      icon: <FlagIcon />,
      path: '/settings/proposal-statuses',
      color: theme.palette.info.main,
    },
    {
      id: 'tags',
      title: t('settings.titles.tags'),
      description: t('settings.descriptions.tags'),
      icon: <LocalOfferIcon />,
      path: '/settings/tags',
      color: theme.palette.warning.main,
    },
    {
      id: 'paymentPlanTemplates',
      title: t('settings.titles.paymentPlanTemplates'),
      description: t('settings.descriptions.paymentPlanTemplates'),
      icon: <PaymentIcon />,
      path: '/settings/payment-plan-templates',
      color: theme.palette.primary.main,
    },
    {
      id: 'pricingProcess',
      title: t('settings.titles.pricingProcess'),
      description: t('settings.descriptions.pricingProcess'),
      icon: <AssessmentIcon />,
      path: '/settings/pricing-process',
      color: theme.palette.info.main,
    },
    {
      id: 'invoiceCodes',
      title: t('settings.titles.invoiceCodes'),
      description: t('settings.descriptions.invoiceCodes'),
      icon: <ReceiptIcon />,
      path: '/settings/invoice-codes',
      color: theme.palette.success.main,
    },
    {
      id: 'bonusRevenueTypes',
      title: t('settings.titles.bonusRevenueTypes', 'Ciro Türleri'),
      description: t('settings.descriptions.bonusRevenueTypes',
        'Prim hesaplamalarında kullanılan ciro türleri ve kat sayıları'),
      icon: <PercentIcon />,
      path: '/settings/bonus-revenue-types',
      color: theme.palette.success.main,
    },
    {
      id: 'userBonusCoefficients',
      title: t('settings.titles.userBonusCoefficients'),
      description: t('settings.descriptions.userBonusCoefficients'),
      icon: <PercentIcon />,
      path: '/settings/kullanici-prim-katsayilari',
      color: theme.palette.warning.main,
    },
    {
      id: 'documentBankTags',
      title: t('settings.titles.documentBankTags', 'Doküman Etiketleri'),
      description: t(
        'settings.descriptions.documentBankTags',
        'Doküman bankasında kullanılacak etiketleri yönet',
      ),
      icon: <LocalOfferIcon />,
      path: '/settings/document-bank-tags',
      color: theme.palette.info.main,
    },
  ];

  const settingsCards = allSettingsCards.filter(card => {
    if (card.requiresRoot && user?.role !== 'ROOT') {
      return false;
    }
    return true;
  });

  const handleCardClick = card => {
    navigate(card.path);
  };

  return (
    <PageContainer currentPage={t('pages.settings')}>
      <Box sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('settings.titles.main')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('settings.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ px: 2, pb: 2 }}>
        {settingsCards.map(card => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={card.id}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  borderColor: card.color,
                },
              }}
              onClick={() => handleCardClick(card)}
            >
              <CardContent
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      backgroundColor: `${card.color}15`,
                      color: card.color,
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.75rem',
                      },
                    }}
                  >
                    {card.icon}
                  </Avatar>

                  <ArrowForwardIosIcon
                    sx={{
                      color: 'text.secondary',
                      fontSize: '1rem',
                      transition: 'transform 0.3s ease',
                      '.MuiCard-root:hover &': {
                        transform: 'translateX(4px)',
                        color: card.color,
                      },
                    }}
                  />
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {card.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flexGrow: 1,
                    lineHeight: 1.5,
                  }}
                >
                  {card.description}
                </Typography>

                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: card.color,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('settings.manage')} →
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </PageContainer>
  );
};

export default SettingsPage;
